import React from "react";
import { AlertTriangle, HeartHandshake } from "lucide-react-native";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
  SecondaryButton,
} from "@louise/moon-ui";
import { semanticColors } from "@louise/theme/tokens";

export type ConfirmDialogVariant = "handoff" | "destructive";

type Props = {
  visible: boolean;
  variant: ConfirmDialogVariant;
  title: string;
  body: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  closeLabel: string;
};

/**
 * In-app confirmation dialog for the concierge quote workflow, on the shared
 * moon-ui Dialog. Replaces `window.confirm` so patients get an app-native
 * dialog on web instead of the browser's stock prompt.
 *
 * Two variants:
 *  - `handoff`: primary confirm, hand-holding tone
 *  - `destructive`: destructive confirm, warning tone (cancel / start over)
 */
export function PharmacyConciergeConfirmModal({
  visible,
  variant,
  title,
  body,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  closeLabel,
}: Props) {
  const isDestructive = variant === "destructive";
  const LeadingIcon = isDestructive ? AlertTriangle : HeartHandshake;

  return (
    <Dialog
      visible={visible}
      onClose={onCancel}
      layout="centered"
      closeButton="minimal"
      closeAccessibilityLabel={closeLabel}
      closeAction="close_concierge_confirm"
      backdropPressLabel="dismiss_concierge_confirm_backdrop"
    >
      <DialogHeader
        leading={
          <LeadingIcon
            size={22}
            color={
              isDestructive
                ? semanticColors.text.destructive
                : semanticColors.text.secondary
            }
            strokeWidth={1.8}
          />
        }
        title={title}
        subtitle={body}
      />
      <DialogActions style={{ marginTop: 20 }}>
        <SecondaryButton
          label={cancelLabel}
          action="access.pharmacy.concierge_confirm_cancel"
          onPress={onCancel}
          flex={1}
        />
        {isDestructive ? (
          <SecondaryButton
            variant="destructive"
            label={confirmLabel}
            action="access.pharmacy.concierge_confirm_destructive"
            onPress={onConfirm}
            flex={1}
          />
        ) : (
          <PrimaryButton
            label={confirmLabel}
            action="access.pharmacy.concierge_confirm"
            onPress={onConfirm}
            flex={1}
          />
        )}
      </DialogActions>
    </Dialog>
  );
}
